import React, { useState, useEffect } from 'react';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import ProgressBar from '@/components/ui/ProgressBar';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import {
  RiArrowLeftLine as BackIcon,
  RiDownloadLine as DownloadIcon,
  RiCheckLine as CheckIcon,
  RiErrorWarningLine as WarningIcon,
  RiCloseLine as CloseIcon,
} from 'react-icons/ri';
import { LettersApi } from '@/api';
import type { BatchProgress } from '@/types/letters';
import { analytics } from '@/utils/analytics';
import { ANALYTICS_EVENTS } from '@/utils/analytics-events';

interface BatchLetterProgressProps {
  batchId: string;
  onBack: () => void;
  onComplete?: (progress: BatchProgress) => void;
}

const BatchLetterProgress: React.FC<BatchLetterProgressProps> = ({
  batchId,
  onBack,
  onComplete,
}) => {
  const [progress, setProgress] = useState<BatchProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<boolean>(false);

  useEffect(() => {
    let cleanup: (() => void) | undefined;

    cleanup = LettersApi.streamBatchProgress(
      batchId,
      batchProgress => {
        setProgress(batchProgress);

        // Call onComplete when batch is finished
        if (
          batchProgress.status === 'completed' ||
          batchProgress.status === 'partial_failure'
        ) {
          // Track batch completion
          analytics.track(ANALYTICS_EVENTS.LETTER_GENERATED, {
            batch_id: batchId,
            total_letters: batchProgress.total_letters,
            completed_count: batchProgress.completed_count,
            failed_count: batchProgress.failed_count,
            status: batchProgress.status,
            is_batch: true,
          });

          onComplete?.(batchProgress);
        }
      },
      err => {
        console.error('Batch progress error:', err);
        setError('Failed to track batch progress');
      }
    );

    return () => {
      cleanup?.();
    };
  }, [batchId, onComplete]);

  const handleDownload = async () => {
    if (!progress || progress.completed_count === 0) {
      return;
    }

    try {
      setDownloading(true);
      const blob = await LettersApi.downloadBatchLetters(batchId);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `letters_batch_${batchId.substring(0, 8)}_${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      // Track batch download
      analytics.track(ANALYTICS_EVENTS.ARCHIVE_DOWNLOADED, {
        batch_id: batchId,
        item_type: 'letters_batch',
        count: progress.completed_count,
      });
    } catch (err) {
      console.error('Download error:', err);
      setError('Failed to download batch letters');

      analytics.track(ANALYTICS_EVENTS.ARCHIVE_DOWNLOAD_FAILED, {
        batch_id: batchId,
        item_type: 'letters_batch',
        error_message: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setDownloading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { bg: string; text: string; icon: any }> = {
      completed: {
        bg: 'bg-green-100 text-green-800',
        text: 'Completed',
        icon: CheckIcon,
      },
      processing: {
        bg: 'bg-blue-100 text-blue-800',
        text: 'Processing',
        icon: LoadingSpinner,
      },
      failed: {
        bg: 'bg-red-100 text-red-800',
        text: 'Failed',
        icon: CloseIcon,
      },
      partial_failure: {
        bg: 'bg-yellow-100 text-yellow-800',
        text: 'Partial Success',
        icon: WarningIcon,
      },
    };

    const badge = badges[status] || badges.processing;
    const Icon = badge.icon;

    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${badge.bg}`}
      >
        <Icon className="h-3 w-3" />
        {badge.text}
      </span>
    );
  };

  const getIndividualStatusIcon = (status: string) => {
    if (status === 'completed') {
      return <CheckIcon className="h-4 w-4 text-green-600" />;
    } else if (status === 'failed') {
      return <CloseIcon className="h-4 w-4 text-red-600" />;
    } else {
      return <LoadingSpinner size="sm" />;
    }
  };

  if (error) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="inline-flex items-center gap-1"
          >
            <BackIcon className="h-4 w-4" /> Back
          </Button>
          <h3 className="text-lg font-semibold text-neutral-900">
            Batch Progress
          </h3>
        </div>
        <Card padding="lg" className="bg-red-50 border-red-200">
          <div className="flex items-start gap-3">
            <CloseIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-red-900">Error</h4>
              <p className="text-sm text-red-800 mt-1">{error}</p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (!progress) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
        <span className="ml-3 text-neutral-600">Loading batch progress...</span>
      </div>
    );
  }

  const isCompleted =
    progress.status === 'completed' || progress.status === 'partial_failure';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="inline-flex items-center gap-1"
          >
            <BackIcon className="h-4 w-4" /> Back
          </Button>
          <h3 className="text-lg font-semibold text-neutral-900">
            Batch Letter Generation
          </h3>
        </div>
        {progress.completed_count > 0 && (
          <Button
            onClick={handleDownload}
            loading={downloading}
            disabled={downloading}
            className="inline-flex items-center gap-2"
          >
            <DownloadIcon className="h-4 w-4" />
            Download ZIP ({progress.completed_count})
          </Button>
        )}
      </div>

      {/* Overall Progress Card */}
      <Card padding="lg">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-base font-semibold text-neutral-900">
                Overall Progress
              </h4>
              <p className="text-sm text-neutral-600 mt-1">
                {progress.completed_count} of {progress.total_letters} letters
                completed
              </p>
            </div>
            {getStatusBadge(progress.status)}
          </div>

          <ProgressBar value={progress.overall_progress} max={100} />

          <div className="grid grid-cols-3 gap-4 pt-2">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {progress.completed_count}
              </div>
              <div className="text-xs text-neutral-600 mt-1">Completed</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {progress.processing_count}
              </div>
              <div className="text-xs text-neutral-600 mt-1">Processing</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">
                {progress.failed_count}
              </div>
              <div className="text-xs text-neutral-600 mt-1">Failed</div>
            </div>
          </div>
        </div>
      </Card>

      {/* Individual Letters Progress */}
      <Card padding="lg">
        <h4 className="text-base font-semibold text-neutral-900 mb-4">
          Individual Letters
        </h4>
        <div className="space-y-3">
          {progress.individual_statuses.map((letterStatus, index) => (
            <div
              key={letterStatus.task_id}
              className="flex items-center gap-3 p-3 rounded-lg bg-neutral-50 border border-neutral-200"
            >
              <div className="flex-shrink-0">
                {getIndividualStatusIcon(letterStatus.status)}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <h5 className="text-sm font-medium text-neutral-900 truncate">
                    {letterStatus.referee_name || `Letter #${index + 1}`}
                  </h5>
                  <span className="text-xs text-neutral-600 ml-2">
                    {letterStatus.progress}%
                  </span>
                </div>
                <ProgressBar
                  value={letterStatus.progress}
                  max={100}
                  size="sm"
                />
                {letterStatus.error_message && (
                  <p className="text-xs text-red-600 mt-1">
                    {letterStatus.error_message}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Success Message */}
      {isCompleted && (
        <Card padding="md" className="bg-green-50 border-green-200">
          <div className="flex items-start gap-3">
            <CheckIcon className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-green-900">
                {progress.status === 'completed'
                  ? 'Batch Generation Complete!'
                  : 'Batch Generation Completed with Some Failures'}
              </h4>
              <p className="text-sm text-green-800 mt-1">
                {progress.completed_count} letter(s) generated successfully.
                {progress.failed_count > 0 &&
                  ` ${progress.failed_count} letter(s) failed.`}
              </p>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default BatchLetterProgress;
